import { OptionsGroupDeprecated, SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react'
import { Close } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import styles from './selectMultiplePills.module.scss'

interface SelectMultiplePillsProps {
    selectOptions: SelectOptionsDeprecated
    selectValues: Array<string>
    setSelectValues: (values: Array<string>) => void
    isParent: boolean
    className?: string
}

const SelectMultiplePills: FunctionComponent<SelectMultiplePillsProps> = ({
    selectOptions,
    selectValues,
    setSelectValues,
    isParent,
    className: propClassName = ''
}: SelectMultiplePillsProps) => {
    const { t } = useTranslation()

    const borderStyle: string = isParent ? styles.ParentBorder : styles.DaycareBorder
    const backgroundStyle: string = isParent ? styles.ParentBackground : styles.DaycareBackground

    const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null)
    const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false)
    const [canScrollRight, setCanScrollRight] = useState<boolean>(false)

    function updateScrollShadows(): void {
        const container: HTMLDivElement | null = containerRef.current
        if (!container) return

        setCanScrollLeft(container.scrollLeft > 0)
        setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 1)
    }

    function handlePillClick(optionValue: string): void {
        if (selectValues.includes(optionValue)) {
            setSelectValues(selectValues.filter((value: string): boolean => value !== optionValue))
        } else {
            setSelectValues([...selectValues, optionValue])
        }
    }

    const options: SelectOptionDeprecated[] = selectOptions.flatMap(
        (group: OptionsGroupDeprecated): SelectOptionDeprecated[] => group.options
    )

    useEffect((): (() => void) => {
        updateScrollShadows()

        window.addEventListener('resize', updateScrollShadows)
        return () => window.removeEventListener('resize', updateScrollShadows)
    }, [options.length])

    return (
        <div className={`${styles.Wrapper} ${propClassName}`}>
            <div
                ref={containerRef}
                onScroll={updateScrollShadows}
                className={`${styles.PillsContainer} ${canScrollLeft ? styles.ShadowLeft : ''} ${canScrollRight ? styles.ShadowRight : ''}`}
            >
                {options.map((option: SelectOptionDeprecated) => (
                    <div
                        key={option.value}
                        className={`${styles.Pill} ${selectValues.includes(option.value) ? `${borderStyle} ${backgroundStyle}` : ''}`}
                        onClick={() => !option.disabled && handlePillClick(option.value)}
                    >
                        {option.name}
                    </div>
                ))}
            </div>

            {selectValues.length > 0 && (
                <button
                    type='button'
                    className={styles.ClearButton}
                    onClick={() => setSelectValues([])}
                    aria-label={t('reset')}
                    title={t('reset')}
                >
                    <Close fontSize='small' />
                </button>
            )}
        </div>
    )
}

export default SelectMultiplePills
